import { useState } from "react";
import type { FormEvent } from "react";
import { siteMeta, topics } from "@/mocks/guage";

const FORM_ID = "dahaogjp14a1h3mr6h5g";
const SUBMIT_ADDR = "https://readdy.ai/api/form/dahaogjp14a1h3mr6h5g";

export default function SubscribeSection() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const raw = new FormData(formEl);

    const honeypot = String(raw.get("company_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      return;
    }

    raw.delete("company_alt");
    const payload = new URLSearchParams();
    raw.forEach((value, key) => {
      payload.append(key, String(value));
    });

    setStatus("loading");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        formEl.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch (error) {
      setStatus("error");
      setFormError(error instanceof Error ? error.message : "网络异常，请稍后重试。");
    }
  };

  return (
    <section id="contact" className="w-full bg-background-50 py-16 md:py-24">
      <div className="w-full px-4 md:px-6">
        <div className="rounded-lg overflow-hidden border border-background-200 bg-background-100">
          <div className="grid grid-cols-1 lg:grid-cols-12">
            <div className="lg:col-span-5 p-6 md:p-10 bg-foreground-950">
              <span className="inline-flex items-center gap-2 text-accent-400 text-xs md:text-sm font-medium">
                <i className="ri-mail-send-line"></i>
                吃瓜日报
              </span>
              <h2 className="mt-4 font-heading text-3xl md:text-4xl text-background-50 leading-tight">
                每周一封，
                <br />
                把最值得回看的瓜送上门
              </h2>
              <p className="mt-4 text-sm md:text-base text-background-50/70 leading-relaxed">
                精选当周神点评、名场面和金句弹幕，五分鐘读完，不刷屏、不打扰。
              </p>

              <ul className="mt-7 space-y-3">
                {["每周一期，周五傍晚发出", "只放干货，不放广告", "随时可退，一次点击即可"].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-sm text-background-50/85">
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-primary-500 text-background-50 shrink-0">
                      <i className="ri-check-line text-xs"></i>
                    </span>
                    {item}
                  </li>
                ))}
              </ul>

              <div className="mt-8 pt-6 border-t border-background-50/15">
                <p className="text-xs text-background-50/55">也可以直接来信</p>
                <a
                  href={`mailto:${siteMeta.email}`}
                  rel="nofollow"
                  className="mt-1.5 inline-flex items-center gap-2 text-sm text-background-50 hover:text-accent-400 transition-colors"
                >
                  <i className="ri-mail-line"></i>
                  {siteMeta.email}
                </a>
              </div>
            </div>

            <div className="lg:col-span-7 p-6 md:p-10">
              <h3 className="font-heading text-2xl md:text-3xl text-foreground-950">订阅吃瓜日报</h3>
              <p className="mt-2 text-sm text-foreground-600">
                留下邮箱和最想看的分类，我们下期就安排。
              </p>

              <form
                id={FORM_ID}
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-6 space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="sub-name" className="block text-sm text-foreground-700 mb-2">
                      称呼
                    </label>
                    <input
                      id="sub-name"
                      name="name"
                      type="text"
                      required
                      autoComplete="off"
                      placeholder="怎么称呼你"
                      className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
                    />
                  </div>
                  <div>
                    <label htmlFor="sub-email" className="block text-sm text-foreground-700 mb-2">
                      邮箱
                    </label>
                    <input
                      id="sub-email"
                      name="email"
                      type="email"
                      required
                      autoComplete="off"
                      placeholder="you@example.com"
                      className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="sub-topic" className="block text-sm text-foreground-700 mb-2">
                    最想看的分类
                  </label>
                  <select
                    id="sub-topic"
                    name="topic"
                    defaultValue={topics[0].name}
                    className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30 cursor-pointer"
                  >
                    {topics.map((topic) => (
                      <option key={topic.name} value={topic.name}>
                        {topic.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="sub-message" className="block text-sm text-foreground-700 mb-2">
                    想对瓜哥说的一句
                  </label>
                  <textarea
                    id="sub-message"
                    name="message"
                    rows={3}
                    maxLength={500}
                    placeholder="可以点名你想拆的热点，也可以只是打个招呼"
                    className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30 resize-none"
                  ></textarea>
                </div>

                <input
                  type="text"
                  name="company_alt"
                  className="alt-contact-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <i className={status === "loading" ? "ri-loader-4-line animate-spin" : "ri-mail-send-line"}></i>
                    {status === "loading" ? "提交中…" : "立即订阅"}
                  </button>
                  <span className="text-xs text-foreground-500">我们只用它发送简报，不会用于其他用途。</span>
                </div>

                {status === "success" && (
                  <p className="flex items-center gap-2 px-4 py-3 rounded-md bg-secondary-100 border border-secondary-200 text-sm text-secondary-800">
                    <i className="ri-checkbox-circle-line"></i>
                    订阅成功，下一期吃瓜日报会准时发到你的邮箱。
                  </p>
                )}
                {status === "error" && (
                  <p className="flex items-center gap-2 px-4 py-3 rounded-md bg-primary-100 border border-primary-200 text-sm text-primary-800">
                    <i className="ri-error-warning-line"></i>
                    {formError}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}